
import React from "react";
import { Row, Col, Card } from "antd";
import { useNavigate } from "react-router-dom";
import {
  BarChart2,
  FileText,
  Users,
  Activity,
  DollarSign,
  Folder,
  User,
  TrendingUp,
  PieChart,
  FileText as Receipt, 
  File as Rocket,
} from "react-feather";
import { FileOutlined, BarChartOutlined, UserOutlined, AccountBookOutlined as Beds } from "@ant-design/icons";
const categoryColors = {
  "Operations & Performance": "#2563eb",
  "Flash Reports": "#007bff",
  "Reservation & Occupancy Reports": "#fd7e14",
  "Financial Reports": "#ffc107",
  "Operational & Guest Insights": "#dc3545",
  "Distribution & Source Analytics": "#6610f2",
  "OTA Reports": "#6610f2",
};

const frontOfficeReports = [
  {
    category: "Operations & Performance",
    reports: [
      {
        icon: <BarChart2 />,
        title: "Daily Flash Report",
        description: "Unified daily revenue, occupancy, payment and operational snapshot.",
        link: "/hospitality/daily-flash-report",
        disabled: true,
        statusLabel: "Coming soon",
      },
      { icon: <Activity />, title: "Shift End Summary", description: "Summary of front office activities at shift closure.", link: "/hospitality/shift-end-summary" },
      { icon: <TrendingUp />, title: "Sales Performance and Occupancy", description: "Comprehensive sales performance and occupancy analysis.", link: "/hospitality/sales-performance" },
    ],
  },
  {
    category: "Reservation & Occupancy Reports",
    reports: [
      { icon: <Users />, title: "Reservation Report", description: "Track confirmed, canceled, and tentative reservations.", link: "/hospitality/reservation-report" },
      { icon: <User />, title: "Occupancy Report", description: "Overview of occupied vs available rooms for any period.", link: "/hospitality/occupancy-report" },
      { icon: <FileText />, title: "Room Status Report", description: "Tracks vacant, occupied, and maintenance rooms.", link: "/hospitality/room-status-report" },
    ],
  },
  {
    category: "Financial Reports",
    reports: [
      {
        icon: <DollarSign />,
        title: "Daily Revenue Report",
        description: "Breakdown of daily earnings from rooms and services.",
        link: "/hospitality/daily-revenue-analysis",
        disabled: true,
        statusLabel: "Coming soon",
      },
      {
        icon: <TrendingUp />,
        title: "Payment Mode Report",
        description: "Payment collection and settlement mix by payment method.",
        link: "/hospitality/payment-mode-report",
        disabled: true,
        statusLabel: "Coming soon",
      },
      { icon: <Activity />, title: "Refund & Reconciliation", description: "Central refund and reconciliation control across StayX and connected booking flows.", link: "/hospitality/refunds" },
      {
        icon: <Folder />,
        title: "Tax Compliance Report",
        description: "Overview of collected taxes, GST, and VAT.",
        link: "/reports/tax-compliance",
        disabled: true,
        statusLabel: "Coming soon",
      },
      { icon: <BarChart2 />, title: "Room Wise Sales Report", description: "Detailed revenue report for each room category.", link: "/hospitality/roomno-wise-sales" },
    ],
  },
  {
    category: "Operational & Guest Insights",
    reports: [
      { icon: <Users />, title: "Guest CRM", description: "Guest relationships, returning visitors, stay history and secure imports.", link: "/hospitality/guest-database" },
      { icon: <FileText />, title: "Room Sold Reports", description: "Details of rooms sold with pricing and tax breakdowns.", link: "/hospitality/daily-room-sold" },

        { icon: <Receipt />, title: "Check Out / Guest Billing",  description: "Details of Guest check out Billing summary with tax breakdown.",link: "/hospitality/front-office-billings", category: "Flash Reports" },

    ],
  },
  {
    category: "Distribution & Source Analytics",
    reports: [
      {
        icon: <TrendingUp />,
        title: "Booking Source Performance",
        description: "Compare Airbnb, Booking.com, Instagram, direct, referral and other sources by bookings, revenue, ADR and cancellations.",
        link: "/hospitality/booking-source-performance",
      },
      { icon: <PieChart />, title: "OTA Booking Volume Report", description: "Breakdown of bookings from different OTA platforms.", link: "/hospitality/ota-booking-volume" },
      { icon: <TrendingUp />, title: "OTA Revenue Performance", description: "Tracks revenue generated from OTA partners.", link: "/hospitality/ota-booking-revenue" },
      {
        icon: <Users />,
        title: "OTA Customer Insights",
        description: "Demographics and behavior of OTA guests.",
        link: "/reports/ota-customer-insights",
        disabled: true,
        statusLabel: "Coming soon",
      },
    ],
  },
];

const quickLinks = [
  { icon: <Activity />, title: "Shift End Summary", link: "/hospitality/shift-end-summary", category: "Operations & Performance" },
  { icon: <Receipt />, title: "Check Out / Guest Billing", link: "/hospitality/front-office-billings", category: "Operational & Guest Insights" },
  { icon: <TrendingUp />, title: "Sales Performance and Occupancy", link: "/hospitality/sales-performance", category: "Operations & Performance" },
  { icon: <Users />, title: "Reservation Report", link: "/hospitality/reservation-report", category: "Reservation & Occupancy Reports" },
  { icon: <User />, title: "Occupancy Report", link: "/hospitality/occupancy-report", category: "Reservation & Occupancy Reports" },
  { icon: <FileText />, title: "Room Status Report", link: "/hospitality/room-status-report", category: "Reservation & Occupancy Reports" },
  { icon: <Activity />, title: "Refund & Reconciliation", link: "/hospitality/refunds", category: "Financial Reports" },
  { icon: <BarChart2 />, title: "Room Type Wise Sales Report", link: "/hospitality/daily-room-sold", category: "Financial Reports" },
  { icon: <Users />, title: "Guest CRM", link: "/hospitality/guest-database", category: "Operational & Guest Insights" },
  { icon: <FileText />, title: "Daily Room Sold Reports", link: "/hospitality/daily-room-sold", category: "Operational & Guest Insights" },
  { icon: <TrendingUp />, title: "Booking Source Performance", link: "/hospitality/booking-source-performance", category: "Distribution & Source Analytics" },
  { icon: <PieChart />, title: "OTA Booking Volume Report", link: "/hospitality/ota-booking-volume", category: "Distribution & Source Analytics" },
  { icon: <TrendingUp />, title: "OTA Revenue Performance", link: "/hospitality/ota-booking-revenue", category: "Distribution & Source Analytics" },
];

const FrontOfficeReports = ({ searchQuery }) => {
  const navigate = useNavigate();

  const handleCardClick = (link) => {
    navigate(link);
  };

  const filteredReports = frontOfficeReports.map((category) => ({
    ...category,
    reports: category.reports.filter(
      (report) =>
        report.title.toLowerCase().includes(searchQuery) ||
        report.description.toLowerCase().includes(searchQuery)
    ),
  })).filter((category) => category.reports.length > 0);

  const filteredQuickLinks = quickLinks.filter(
    (link) =>
      link.title.toLowerCase().includes(searchQuery) ||
      link.category.toLowerCase().includes(searchQuery)
  );

  return (
    <Row gutter={[16, 16]}>
      <Col xs={24} md={16} lg={18}>
        {filteredReports.map((category, index) => (
          <div key={index} className="category-section mb-4">
            <h4
              className="category-title"
              style={{
                color: "#031b4e",
                fontSize: "20px",
                fontWeight: "400",
                marginBottom: "20px",
                display: "inline-block",
                borderBottom: `3px solid ${categoryColors[category.category]}`,
                paddingBottom: "5px",
              }}
            >
              {category.category}
            </h4>
            <Row gutter={[16, 16]}>
              {category.reports.map((report, idx) => (

<Col key={idx} xs={24} sm={12} md={12} lg={8}>
  <Card
    hoverable={!report.disabled}
    style={{
      position: "relative",
      borderLeft: `4px solid ${categoryColors[category.category]}`,
      cursor: report.disabled ? "not-allowed" : "pointer",
      borderRadius: "8px",
      padding: "12px",
      opacity: report.disabled ? 0.72 : 1,
      background: report.disabled ? "#fbfcfe" : "#ffffff",
    }}
    onClick={report.disabled ? undefined : () => handleCardClick(report.link)}
  >
    <div
      style={{
        display: "flex",
        alignItems: "center",
        marginBottom: "8px",
      }}
    >
      <span
        style={{
          color: categoryColors[category.category],
          fontSize: "20px",
          marginRight: "8px",
        }}
      >
        {report.icon}
      </span>
      <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
        <h5
          style={{
            fontSize: "16px",
            fontWeight: "600",
            margin: 0,
          }}
        >
          {report.title}
        </h5>
        {report.disabled && (
          <span
            style={{
              flexShrink: 0,
              padding: "3px 7px",
              borderRadius: 999,
              background: "#f2f4f7",
              border: "1px solid #e4e7ec",
              color: "#667085",
              fontSize: 9,
              fontWeight: 800,
              letterSpacing: ".05em",
              textTransform: "uppercase",
            }}
          >
            {report.statusLabel || "Coming soon"}
          </span>
        )}
      </div>
    </div>
    <p style={{ fontSize: "14px", color: "#666", margin: 0 }}>
      {report.description}
    </p>
  </Card>
</Col>

              ))}
            </Row>
          </div>
        ))}
      </Col>
      <Col xs={24} md={8} lg={6}>
        <div
          style={{
            backgroundColor: "#f8f9fa",
            padding: "16px",
            borderRadius: "8px",
            borderLeft: "4px solid #007bff",
            minHeight: "100%",
            height: "fit-content",
            overflowY: "auto",
          }}
        >
          <h4
            style={{
              fontSize: "16px",
              fontWeight: "600",
              color: "#031b4e",
              marginBottom: "16px",
              display: "flex",
              alignItems: "center",
            }}
          >
            <Rocket size={18} style={{ marginRight: "8px", color: "#007bff" }} />
            Quick Access
          </h4>
<ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
  {filteredQuickLinks.map((link, idx) => (
    <li
      key={idx}
      onClick={() => handleCardClick(link.link)}
      style={{
        display: "flex",
        alignItems: "center",
        padding: "8px",
        marginBottom: "0px",
        borderBottom: idx !== filteredQuickLinks.length - 1 ? "1px solid #eee" : "none",
        cursor: "pointer",
        transition: "background-color 0.2s",
      }}
      className="quick-link-item"
    >
      <span
        style={{
          color: categoryColors[link.category],
          marginRight: "8px",
          fontSize: "16px",
        }}
      >
        {link.icon}
      </span>
      <span style={{ fontSize: "14px", color: "#333" }}>{link.title}</span>
    </li>
  ))}
</ul>

        </div>
      </Col>
    </Row>
  );
};

export default FrontOfficeReports;
